import { Button, Card, Panel, Table } from '@/components/Bicester';
import { Cell, DateTimeCell, LevelCell } from '@/components/Bicester/Table/Cells';
import { Form } from '@/components/formik-rsuite';
import useParkingBasic from '@/hooks/preload/useParkingBasic';
import useGePcsList from '@/hooks/preload/useGePcsList';
import useRouter from '@/hooks/useRouter';
import { fetchMemberInfo, fetchParking, fetchParkingList } from '@/services/account';
import { useRequest } from 'ahooks';
import { Alert } from 'rsuite';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { SERVICE_STATUS } from '@/services/constants';
import useContentHeight from '@/hooks/useContentHeight';
import { LAYOUT_CONFIG } from '@/constants/layoutConfig';
import { IconButton } from 'rsuite5';
import Image from '@/components/Bicester/Image';
import _ from 'lodash';
import { BICESTER_FULL_DATE_HOURS_MINUTES_SECOND } from '@/constants/dateFormat';
import { useTable } from '@/hooks';

interface ParkingForm {
  reason_id: number; // 停车原因
  car_number: string; // 车牌号
}

const Parking = () => {
  const { query } = useRouter();
  const { data: parkingBasic, loading: parkingBasicLoading } = useParkingBasic();
  const { data: gePcsList, loading: gePcsListLoading } = useGePcsList();

  const { run: getMemberInfo, loading: memberInfoLoading } = useRequest(fetchMemberInfo, {
    manual: true,
  });
  const { run: addParking, loading: parkingLoading } = useRequest(fetchParking, {
    manual: true,
  });
  const {
    run: getParkingList,
    data: { content_list: parkingListData, current_page, total } = {},
    loading: parkingListLoading,
  } = useRequest<any>(fetchParkingList, { manual: true });

  const { params, onChange, onSearch } = useTable(getParkingList);

  useEffect(() => {
    getMemberInfo(query?.memberid).then((res) => {
      const phone = res?.phone;
      if (phone) {
        listFormik.setFieldValue('phone', phone);
        getParkingList({
          ...params,
          phone,
        });
      }
    });
  }, []);

  const formik = useFormik({
    initialValues: {
      reason_id: null,
      car_number: '',
    } as ParkingForm,
    validationSchema: Yup.object({
      reason_id: Yup.number().typeError('请选择优惠停车原因').required('请选择优惠停车原因'),
      car_number: Yup.string()
        .required('请输入车牌号')
        .min(7, '车牌号至少7位字符')
        .max(8, '车牌号至多8位字符'),
    }),
    async onSubmit(values, actions) {
      const data = Object.assign({}, values, {
        member_id: query?.memberid,
      });
      addParking(data)
        .then((res) => {
          if (res?.status === SERVICE_STATUS.SUCCESS) {
            Alert.success(res?.msg ?? '保存成功');
          }
        })
        .catch((e) => {
          actions.setErrors(e.formError);
        });
    },
  });
  const listFormik = useFormik({
    initialValues: {
      time_range: [],
      phone: '',
      car_number: '',
      creator: null,
    },
    onSubmit(values, actions) {
      const params = Object.assign({}, _.omit(values, 'time_range'), {
        start_at:
          (values?.time_range && values?.time_range?.length === 2 && values?.time_range[0]) || '',
        end_at:
          (values?.time_range && values?.time_range?.length === 2 && values?.time_range[1]) || '',
      });
      onSearch(params);
    },
  });

  return (
    <>
      <Panel
        title="免费优惠停车"
        loading={parkingBasicLoading || gePcsListLoading || memberInfoLoading || parkingLoading}
      >
        <FormikProvider value={formik}>
          <Form>
            <div className="h-full flex flex-col justify-between">
              <div className="flex justify-between">
                <div className="w-[50%] flex">
                  <Card>
                    <Card.Picker
                      label="优惠停车原因"
                      formKey="reason_id"
                      pickerData={parkingBasic?.reason_list}
                      contentWeight={4}
                    />
                  </Card>
                  <Card>
                    <Card.Input label="车牌号" formKey="car_number" contentWeight={4} />
                  </Card>
                </div>
                <Button type="submit" className="h-[38px] px-[70px]">
                  确认提交
                </Button>
              </div>
            </div>
          </Form>
        </FormikProvider>
      </Panel>
      <Panel
        title="历史记录"
        subTitle={`总计 ${total || 0} 条`}
        loading={parkingListLoading}
        dividerHeight={10}
        style={{
          minHeight:
            'calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight) - 195px)',
        }}
      >
        <FormikProvider value={listFormik}>
          <Form>
            <Card horizontal>
              <Card.DateRangePicker label="服务日期" formKey="time_range" />
              <Card.Input label="会员手机号" formKey="phone" />
              <Card.Input label="车牌号" formKey="car_number" />
              <Card.Picker
                label="服务大使"
                formKey="creator"
                pickerData={gePcsList.ge_list}
                searchable
                placement="bottomEnd"
              />
              <IconButton
                type="submit"
                className="flex items-center w-[30px] ml-[10px]"
                style={{ height: 47 }}
              >
                <Image src="/resources/images/match.svg" />
              </IconButton>
            </Card>
          </Form>
        </FormikProvider>
        <Table
          style={{
            marginTop: 20,
            height: `max(${
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 195
            }px, ${
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT +
              LAYOUT_CONFIG.TABLE_PAGINATION_WITH_MARGIN_HEIGHT
            }px})`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          pageInfo={{
            page: current_page,
            total,
          }}
          onChange={onChange}
          data={parkingListData}
          loading={gePcsListLoading || parkingListLoading}
          columns={[
            {
              columnProps: { flexGrow: 2 },
              header: '创建时间',
              cell: (
                <DateTimeCell
                  dataKey="create_at"
                  format={BICESTER_FULL_DATE_HOURS_MINUTES_SECOND}
                />
              ),
            },
            {
              columnProps: { flexGrow: 1 },
              header: '手机尾号',
              cell: <Cell dataKey="phone" />,
            },
            {
              columnProps: { flexGrow: 1 },
              header: '客户姓名',
              cell: <Cell dataKey="name" />,
            },
            {
              columnProps: { flexGrow: 1 },
              header: '会员等级',
              cell: <LevelCell dataKey="level" />,
            },
            {
              columnProps: {
                flexGrow: 1.5,
              },
              header: '车牌号',
              cell: <Cell dataKey="car_number" />,
            },
            {
              columnProps: {
                flexGrow: 1.5,
              },
              header: '停车原因',
              cell: <Cell dataKey="reason" />,
            },
            {
              columnProps: {
                flexGrow: 1.5,
              },
              header: '服务大使',
              cell: <Cell dataKey="creator_name" />,
            },
          ]}
        >
          <Table.Body
            height={Math.max(
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 195,
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT
            )}
          />
          <Table.Pagination />
        </Table>
      </Panel>
    </>
  );
};

export default Parking;
